// src/screens/PartnerHomeScreen.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import "../styles/PartnerHomeScreen.css";

const API_BASE = (() => {
  const clean = (v) =>
    (v || "")
      .trim()
      .replace(/\/+$/, "")
      .replace(/\/api$/, "");

  const envBase = clean(process.env.REACT_APP_API_URL);
  if (envBase) return envBase;

  if (typeof window === "undefined") return "";

  const origin = window.location.origin || "";
  const isCapacitor =
    origin.startsWith("capacitor://") ||
    origin.includes("localhost") ||
    origin.includes("127.0.0.1");

  if (isCapacitor) return "https://pay2pee.onrender.com";
  return origin;
})();

// Shape for empty bathroom/location details
const EMPTY_LOCATION = {
  price: 0,
  accessCode: "",
  description: "",
  hours: "",
  features: [],
  photos: [],
};

export default function PartnerHomeScreen() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  // =========================
  // ✅ ANALYTICS HELPERS (NEW)
  // =========================
  const track = (eventName, params = {}) => {
    try {
      if (typeof window === "undefined") return;
      if (typeof window.gtag !== "function") return;

      window.gtag("event", eventName, {
        ...params,
        app: "pay2pee",
        screen: "partner_home",
      });
    } catch {
      // no-op
    }
  };

  // SPA page_view (Partner Home)
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      if (typeof window.gtag !== "function") return;

      window.gtag("event", "page_view", {
        page_title: "Partner Home",
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.hash,
      });
    } catch {
      // no-op
    }
  }, []);

  // ✅ NEW: compress helper (mobile-friendly)
  // - resizes longest side to maxDim
  // - compresses to jpeg (quality)
  // - returns { dataUrl, blob, bytes }
  const compressImage = (file, { maxDim = 1280, quality = 0.82 } = {}) =>
    new Promise((resolve, reject) => {
      try {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
          URL.revokeObjectURL(url);

          const w = img.width || 1;
          const h = img.height || 1;

          // keep aspect ratio
          const scale = Math.min(1, maxDim / Math.max(w, h));
          const nw = Math.round(w * scale);
          const nh = Math.round(h * scale);

          const canvas = document.createElement("canvas");
          canvas.width = nw;
          canvas.height = nh;

          const ctx = canvas.getContext("2d");
          if (!ctx) return reject(new Error("Canvas not supported"));

          ctx.drawImage(img, 0, 0, nw, nh);

          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error("Compression failed"));
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve({
                  dataUrl: reader.result,
                  blob,
                  bytes: blob.size,
                });
              };
              reader.readAsDataURL(blob);
            },
            "image/jpeg",
            quality
          );
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Could not load image"));
        };

        img.src = url;
      } catch (e) {
        reject(e);
      }
    });

  // Load partner summary (REAL DATA)
  useEffect(() => {
    let cancelled = false;

    const fetchSummary = async () => {
      setLoading(true);
      setErr(null);

      try {
        const t0 = Date.now();

        const res = await axios.get(`${API_BASE}/api/partner/summary`, {
          headers: authHeader,
        });

        if (cancelled) return;

        const incoming = res.data || {};
        const reviews = Array.isArray(incoming.reviews) ? incoming.reviews : [];

        // Rating fallback from reviews if missing
        let rating = incoming.rating;
        if ((rating === undefined || rating === null) && reviews.length > 0) {
          const sum = reviews.reduce(
            (acc, r) => acc + (Number(r.stars) || 0),
            0
          );
          rating = Number((sum / reviews.length).toFixed(1));
        }

        const locationDetails = {
          ...EMPTY_LOCATION,
          ...(incoming.locationDetails || {}),
        };

        const stripeStatusRaw = incoming.stripeStatus || {};
        const stripeStatus = {
          charges_enabled: !!stripeStatusRaw.charges_enabled,
          payouts_enabled: !!stripeStatusRaw.payouts_enabled,
          details_submitted: !!stripeStatusRaw.details_submitted,
        };

        const rawTotal =
          incoming.stripeBalance !== undefined &&
          incoming.stripeBalance !== null
            ? incoming.stripeBalance
            : incoming.currentBalance;

        const rawAvailable =
          incoming.stripeAvailableBalance !== undefined &&
          incoming.stripeAvailableBalance !== null
            ? incoming.stripeAvailableBalance
            : rawTotal;

        const rawPending =
          incoming.stripePendingBalance !== undefined &&
          incoming.stripePendingBalance !== null
            ? incoming.stripePendingBalance
            : null;

        const normalized = {
          partnerId: incoming.partnerId || "",
          email: incoming.email || "",
          name: incoming.name || "",
          address: incoming.address || "",
          isActive: incoming.isActive !== false,
          todayVisits: incoming.todayVisits || 0,

          currentBalance: Number(rawTotal || 0),
          availableBalance: Number(rawAvailable || 0),
          pendingBalance: rawPending !== null ? Number(rawPending || 0) : null,

          lastPayoutDate: incoming.lastPayoutDate || null,
          rating: rating ?? null,
          reviews,
          locationDetails,
          ownerName:
            incoming.ownerName ||
            incoming.name ||
            (incoming.email ? incoming.email.split("@")[0] : "Partner"),
          avatarUrl: incoming.avatarUrl || "",
          stripeAccountId: incoming.stripeAccountId || null,
          stripeStatus,
        };

        setData(normalized);

        // ✅ analytics: summary loaded
        track("partner_summary_loaded", {
          ms: Date.now() - t0,
          has_stripe: !!normalized.stripeAccountId,
          payouts_enabled: !!normalized.stripeStatus?.payouts_enabled,
          charges_enabled: !!normalized.stripeStatus?.charges_enabled,
          is_active: !!normalized.isActive,
          today_visits: Number(normalized.todayVisits || 0),
        });

        // Sync navbar avatar/name
        if (typeof window !== "undefined") {
          localStorage.setItem(
            "userName",
            normalized.ownerName || normalized.name || "Partner"
          );
          localStorage.setItem("avatarUrl", normalized.avatarUrl || "");
          window.dispatchEvent(new Event("auth:update"));
        }
      } catch (e) {
        console.error("GET /partner/summary error:", e);
        setErr("Could not load your partner profile. Try refreshing the page.");

        track("partner_summary_failed", {
          status: e?.response?.status || 0,
          message: String(e?.message || e),
        });

        setData({
          partnerId: "",
          email: "",
          name: "",
          address: "",
          isActive: true,
          todayVisits: 0,
          currentBalance: 0,
          availableBalance: 0,
          pendingBalance: null,
          lastPayoutDate: null,
          rating: null,
          reviews: [],
          locationDetails: { ...EMPTY_LOCATION },
          ownerName: "Partner",
          avatarUrl: "",
          stripeAccountId: null,
          stripeStatus: {
            charges_enabled: false,
            payouts_enabled: false,
            details_submitted: false,
          },
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, token]); // keep as you had it

  // Toggle listing visibility
  const toggleActive = async () => {
    if (!data) return;
    const next = !data.isActive;

    track("partner_toggle_visibility_click", {
      next_active: !!next,
      prev_active: !!data.isActive,
    });

    try {
      await axios.put(
        `${API_BASE}/api/host/visibility`,
        { isActive: next },
        { headers: authHeader }
      );

      track("partner_toggle_visibility_success", {
        active: !!next,
      });
    } catch (e) {
      console.error("Visibility toggle error:", e);

      track("partner_toggle_visibility_failed", {
        status: e?.response?.status || 0,
        message: String(e?.message || e),
      });
    }

    setData((prev) => (prev ? { ...prev, isActive: next } : prev));
  };

  // Stripe onboarding – /partner/onboard-link
  const startOnboarding = async () => {
    track("partner_stripe_onboarding_click", {
      has_stripe: !!data?.stripeAccountId,
    });

    try {
      setConnectLoading(true);

      const res = await axios.post(
        `${API_BASE}/api/partner/onboard-link`,
        {
          email: data?.email || undefined,
        },
        { headers: authHeader }
      );

      const url = res.data?.url;
      if (!url) {
        track("partner_stripe_onboarding_failed", {
          reason: "no_url",
        });
        alert("Could not start Stripe onboarding. Please try again.");
        return;
      }

      track("partner_stripe_onboarding_success", {});
      window.location.href = url;
    } catch (e) {
      console.error("Stripe onboarding error:", e);

      track("partner_stripe_onboarding_failed", {
        status: e?.response?.status || 0,
        message: String(e?.message || e),
      });

      alert("Could not start Stripe onboarding. Please try again.");
    } finally {
      setConnectLoading(false);
    }
  };

  const requestPayout = async () => {
    if (!data) return;

    const rawBalance = data.availableBalance ?? data.currentBalance ?? 0;
    const balance = Number(rawBalance);

    track("partner_payout_click", {
      available_balance: Number(balance || 0),
      payouts_enabled: !!data.stripeStatus?.payouts_enabled,
    });

    if (!balance || balance <= 0) {
      track("partner_payout_blocked", { reason: "no_balance" });
      alert("No available balance for payout yet.");
      return;
    }

    if (!data.stripeStatus?.payouts_enabled) {
      track("partner_payout_blocked", { reason: "payouts_not_enabled" });
      alert("Finish Stripe onboarding and enable payouts to withdraw.");
      return;
    }

    const amountDollars = Math.round(balance * 100) / 100;
    const amountCents = Math.round(amountDollars * 100);

    setPayoutLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/host/payout`,
        {
          amountRequested: amountDollars,
          amountRequestedCents: amountCents,
        },
        { headers: authHeader }
      );

      const newBalanceRaw =
        res.data?.newBalance !== undefined
          ? res.data.newBalance
          : res.data?.balance !== undefined
          ? res.data.balance
          : undefined;

      const newTotal =
        newBalanceRaw !== undefined ? Number(newBalanceRaw) : 0;
      const lastPayoutDate = res.data?.lastPayoutDate || null;

      setData((prev) =>
        prev
          ? {
              ...prev,
              currentBalance: newTotal,
              availableBalance: newTotal,
              pendingBalance: prev.pendingBalance,
              lastPayoutDate: lastPayoutDate || prev.lastPayoutDate,
            }
          : prev
      );

      track("partner_payout_success", {
        amount: Number(amountDollars || 0),
      });

      alert("Payout requested.");
    } catch (e) {
      console.error("Payout error:", e);
      const raw = e.response?.data;
      let msg =
        (typeof raw === "string" && raw) ||
        raw?.error ||
        raw?.message ||
        "Could not request payout. Please try again.";

      track("partner_payout_failed", {
        status: e?.response?.status || 0,
        message: String(msg),
      });

      alert(msg);
    } finally {
      setPayoutLoading(false);
    }
  };

  // Bathroom field helpers
  const updateField = (field, value) => {
    setData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const updateLocationDetail = (field, value) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            locationDetails: {
              ...prev.locationDetails,
              [field]: value,
            },
          }
        : prev
    );
  };

  const updatePrice = (val) => {
    const num = Number(val);
    if (Number.isNaN(num)) return;
    updateLocationDetail("price", num);
  };

  const addPhotoFromPrompt = () => {
    track("partner_photo_add_url_click", {});
    const url = window.prompt(
      "For now, paste a photo URL. (Device uploads will be added in the mobile app.)"
    );
    if (!url) return;

    track("partner_photo_added_url", {});
    setData((prev) =>
      prev
        ? {
            ...prev,
            locationDetails: {
              ...prev.locationDetails,
              photos: [...(prev.locationDetails.photos || []), url],
            },
          }
        : prev
    );
  };

  // ✅ UPDATED: mobile-friendly upload (auto compress instead of blocking)
  const handlePhotoFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    track("partner_photo_upload_selected", {
      bytes: Number(file.size || 0),
      type: String(file.type || ""),
    });

    try {
      // Try a couple compression passes to hit the target
      const TARGET_BYTES = 420 * 1024; // ~420KB target (safe under your old 400KB rule)
      let pass = await compressImage(file, { maxDim: 1280, quality: 0.82 });

      if (pass.bytes > TARGET_BYTES) {
        pass = await compressImage(file, { maxDim: 1080, quality: 0.72 });
      }
      if (pass.bytes > TARGET_BYTES) {
        pass = await compressImage(file, { maxDim: 960, quality: 0.62 });
      }

      if (!pass.dataUrl) throw new Error("No dataUrl after compression");

      track("partner_photo_upload_added", {
        bytes: Number(pass.bytes || 0),
      });

      setData((prev) =>
        prev
          ? {
              ...prev,
              locationDetails: {
                ...prev.locationDetails,
                photos: [...(prev.locationDetails.photos || []), pass.dataUrl],
              },
            }
          : prev
      );
    } catch (err) {
      console.error("Photo compress/upload failed:", err);
      alert(
        "Could not process that photo. Try choosing a different image or taking a screenshot (usually smaller)."
      );
    } finally {
      e.target.value = "";
    }
  };

  const removePhoto = (index) => {
    track("partner_photo_removed", { index });
    setData((prev) =>
      prev
        ? {
            ...prev,
            locationDetails: {
              ...prev.locationDetails,
              photos: (prev.locationDetails.photos || []).filter(
                (_, i) => i !== index
              ),
            },
          }
        : prev
    );
  };

  const saveLocation = async () => {
    if (!data) return;

    track("partner_save_location_click", {
      photos_count: Number((data.locationDetails.photos || []).length),
      price: Number(data.locationDetails.price || 0),
      has_access_code: !!(data.locationDetails.accessCode || "").trim(),
      has_hours: !!(data.locationDetails.hours || "").trim(),
    });

    try {
      const payload = {
        name: data.name,
        address: data.address,
        accessCode: data.locationDetails.accessCode,
        price: data.locationDetails.price,
        description: data.locationDetails.description,
        features: data.locationDetails.features || [],
        photos: data.locationDetails.photos || [],
        hours: data.locationDetails.hours || "",
      };

      await axios.put(`${API_BASE}/api/partner/location`, payload, {
        headers: authHeader,
      });

      track("partner_save_location_success", {});
      alert("Bathroom details saved.");
    } catch (e) {
      console.error("Save location error:", e);

      track("partner_save_location_failed", {
        status: e?.response?.status || 0,
        message: String(e?.message || e),
      });

      if (e.response?.status === 413) {
        alert(
          "This photo is too large for the server to accept. Please try again (it will auto-compress), or use a smaller image."
        );
      } else {
        alert("Could not save bathroom details. Please try again.");
      }
    }
  };

  if (loading || !data) {
    return (
      <div className="partner-container">
        <div className="location-form">
          <h2>Loading your partner profile…</h2>
          <p>Please wait a moment.</p>
        </div>
      </div>
    );
  }

  const s = data.stripeStatus || {};
  const payoutsReady = !!s.payouts_enabled;
  const chargesReady = !!s.charges_enabled;

  const totalBalance = Number(data.currentBalance || 0);
  const availableBalance = Number(
    data.availableBalance ?? data.currentBalance ?? 0
  );

  return (
    <div className="partner-container">
      {err && <div className="demo-banner">{err}</div>}

      <div className="stripe-status-banner">
        <div className="stripe-status-text">
          <strong>Payouts</strong>
          <span className={chargesReady ? "ok" : "warn"}>
            {chargesReady ? "Payments enabled" : "Payments not ready"}
          </span>
          <span>•</span>
          <span className={payoutsReady ? "ok" : "warn"}>
            {payoutsReady ? "Payouts enabled" : "Payouts not ready"}
          </span>
          <span>•</span>
          <span className={s.details_submitted ? "ok" : "warn"}>
            {s.details_submitted ? "Details submitted" : "Action required"}
          </span>
        </div>

        <button
          className="signup-button"
          onClick={startOnboarding}
          disabled={connectLoading}
        >
          {connectLoading
            ? "Contacting Stripe…"
            : payoutsReady
            ? "Edit payout settings"
            : "Set up Stripe payouts"}
        </button>
      </div>

      <div className="header-section">
        <div>
          <h1>{data.name || "Your Bathroom Name"}</h1>
          <p className="toggle-hint">
            This is what guests see on the map and the listing.
          </p>
        </div>
        <div>
          <label className="status-label">
            Status:
            <span className={data.isActive ? "status-active" : "status-paused"}>
              {data.isActive ? "Active" : "Paused"}
            </span>
          </label>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={data.isActive}
              onChange={toggleActive}
            />
            <span className="slider" />
          </label>
          <p className="toggle-hint">
            Pause this listing to hide it from guests on the map.
          </p>
        </div>
      </div>

      <div className="stats-container">
        <div className="stat-card">
          <h3>Today’s Guests</h3>
          <p>{data.todayVisits ?? 0}</p>
        </div>
        <div className="stat-card">
          <h3>Balance</h3>
          <p>${totalBalance.toFixed(2)}</p>
          <p className="toggle-hint">
            Available now: ${availableBalance.toFixed(2)}
          </p>
          <button
            className="signup-button"
            style={{ marginTop: 8 }}
            onClick={requestPayout}
            disabled={payoutLoading || !payoutsReady || availableBalance <= 0}
            title={
              !payoutsReady
                ? "Finish Stripe onboarding first"
                : availableBalance <= 0
                ? "Waiting for funds to become available"
                : ""
            }
          >
            {payoutLoading ? "Processing…" : "Payout to bank"}
          </button>
          {data.lastPayoutDate && (
            <p className="toggle-hint">
              Last payout:{" "}
              {new Date(data.lastPayoutDate).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="stat-card">
          <h3>Rating</h3>
          <p>{data.rating ? `⭐ ${data.rating}` : "No rating yet"}</p>
          <p className="toggle-hint">
            Ratings update as guests start leaving reviews.
          </p>
        </div>
      </div>

      <div className="layout-grid">
        <div className="location-form">
          <h2>Bathroom Details</h2>

          <div className="form-group">
            <label>Bathroom name</label>
            <input
              type="text"
              value={data.name || ""}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="e.g. 'VIP Bathroom – Back of Bar'"
            />
          </div>

          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={data.address || ""}
              onChange={(e) => updateField("address", e.target.value)}
              placeholder="Street, City, State ZIP"
            />
          </div>

          <div className="form-group">
            <label>Access code</label>
            <input
              type="text"
              value={data.locationDetails.accessCode || ""}
              onChange={(e) => updateLocationDetail("accessCode", e.target.value)}
              placeholder="e.g. 3131 or 'See bartender'"
            />
          </div>

          <div className="form-group">
            <label>Price (per visit)</label>
            <input
              type="number"
              min="0"
              step="0.25"
              value={
                data.locationDetails.price !== undefined &&
                data.locationDetails.price !== null
                  ? data.locationDetails.price
                  : ""
              }
              onChange={(e) => updatePrice(e.target.value)}
              placeholder="2.00"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              rows={3}
              value={data.locationDetails.description || ""}
              onChange={(e) => updateLocationDetail("description", e.target.value)}
              placeholder="Describe privacy, cleanliness, and vibe."
            />
          </div>

          <div className="form-group">
            <label>Hours</label>
            <input
              type="text"
              value={data.locationDetails.hours || ""}
              onChange={(e) => updateLocationDetail("hours", e.target.value)}
              placeholder="e.g. Fri–Sat 8pm–2am"
            />
          </div>

          <div className="form-group">
            <label>Photos (max 4)</label>
            <div className="photos-row">
              {(data.locationDetails.photos || []).map((url, idx) => (
                <div key={idx} className="photo-chip">
                  <img className="location-photo" src={url} alt={`Photo ${idx + 1}`} />
                  <button
                    type="button"
                    className="remove-photo"
                    onClick={() => removePhoto(idx)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {(data.locationDetails.photos || []).length < 4 && (
                <>
                  <label className="signup-button">
                    Upload photo
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handlePhotoFileChange}
                    />
                  </label>
                  <button
                    type="button"
                    className="signup-button"
                    style={{ marginLeft: 8 }}
                    onClick={addPhotoFromPrompt}
                  >
                    Add photo by URL
                  </button>
                </>
              )}
            </div>
          </div>

          <button
            className="signup-button"
            style={{ marginTop: 16 }}
            onClick={saveLocation}
          >
            Save bathroom details
          </button>
        </div>

        <div className="location-form">
          <h2>Recent Reviews</h2>
          <div className="reviews-list">
            {data.reviews.length === 0 && (
              <p className="toggle-hint">
                Reviews will show up here once guests start using your
                bathroom and leaving feedback.
              </p>
            )}
            {data.reviews.map((r, i) => (
              <div key={i} className="review-card">
                <div className="review-header">
                  {r.avatar && (
                    <img
                      className="guest-photo"
                      src={r.avatar}
                      alt={r.guestName || "Guest"}
                    />
                  )}
                  <div>
                    <strong>{r.guestName || "Guest"}</strong>
                    {r.timestamp && (
                      <div className="review-date">
                        {new Date(r.timestamp).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="review-rating">
                    <span className="star">★</span>{" "}
                    {Number(r.stars || 0).toFixed(1)}
                  </div>
                </div>
                <p className="review-comment">{r.comment}</p>
              </div>
            ))}
          </div>

          <h3>Features</h3>
          <div className="features-tags">
            {(data.locationDetails.features || []).map((f, i) => (
              <span className="feature-tag" key={i}>
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}